import { createSlice } from "@reduxjs/toolkit";
const STORAGE_KEY = "V2_ReportingPortal";
function loadAuthFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object")
            return null;
        return parsed;
    }
    catch {
        return null;
    }
}
export function saveAuthToStorage(authState) {
    try {
        const payload = {
            isAuthenticated: Boolean(authState?.token),
            user: authState?.user ?? null,
            token: authState?.token ?? null,
            // permissions intentionally excluded — always fetched fresh from /api/auth/me on load
            isOtpVerified: Boolean(authState?.isOtpVerified),
            requiresOtp: Boolean(authState?.requiresOtp),
            otpEntityType: authState?.otpEntityType ?? null,
            otpUserId: authState?.otpUserId ?? null,
            entity_type: authState?.entity_type ?? null,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    }
    catch {
        // Ignore write errors (e.g., storage full, blocked, etc.)
    }
}
export function clearAuthFromStorage() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // Ignore
    }
}
const persisted = loadAuthFromStorage();
const initialState = {
    isAuthenticated: Boolean(persisted?.token),
    user: persisted?.user ?? null,
    token: persisted?.token ?? null,
    permissions: null, // never load from localStorage — always fetched fresh from /api/auth/me
    isOtpVerified: Boolean(persisted?.isOtpVerified),
    requiresOtp: Boolean(persisted?.requiresOtp),
    otpEntityType: persisted?.otpEntityType ?? null,
    otpUserId: persisted?.otpUserId ?? null,
    entity_type: persisted?.entity_type ?? null,
};
const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        /**
         * Call this after a successful login API response.
         * Keep reducers pure: persistence is handled by the store subscriber.
         */
        loginSuccess: (state, action) => {
            const { user, token, permissions, requiresOtp, entity_type } = action.payload || {};
            state.user = user ?? null;
            state.token = token ?? null;
            state.isAuthenticated = Boolean(token);
            state.permissions = permissions ?? null;
            state.requiresOtp = Boolean(requiresOtp);
            state.entity_type = entity_type ?? null;
            // Reset OTP verification state on new login
            state.isOtpVerified = false;
            // Clear OTP pending state if any
            state.otpEntityType = null;
            state.otpUserId = null;
        },
        /**
         * Login response for 2FA enabled users (no user/token yet, only temp_token).
         */
        loginOtpPending: (state, action) => {
            const { token, otpEntityType, otpUserId } = action.payload || {};
            state.isAuthenticated = false;
            state.user = null;
            state.token = token ?? null;
            state.permissions = null;
            state.requiresOtp = true;
            state.isOtpVerified = false;
            state.otpEntityType = otpEntityType ?? null;
            state.otpUserId = otpUserId ?? null;
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.token = null;
            state.permissions = null;
            state.isOtpVerified = false;
            state.requiresOtp = false;
            state.otpEntityType = null;
            state.otpUserId = null;
            state.entity_type = null;
        },
        verifyOtpSuccess: (state) => {
            state.isOtpVerified = true;
        },
        /**
         * OTP verified -> backend returns real session token/user.
         */
        otpLoginSuccess: (state, action) => {
            const { user, token, permissions, entity_type } = action.payload || {};
            state.user = user ?? null;
            state.token = token ?? null;
            state.permissions = permissions ?? null;
            state.isAuthenticated = Boolean(token);
            state.requiresOtp = false;
            state.isOtpVerified = true;
            state.otpEntityType = null;
            state.otpUserId = null;
            state.entity_type = entity_type ?? null;
        },
        /**
         * Update user data and token without resetting auth state.
         * Used for background synchronization of permissions and versions.
         */
        updateUserAndToken: (state, action) => {
            const { user, token, permissions } = action.payload;
            state.user = user;
            state.token = token;
            state.permissions = permissions;
            state.isAuthenticated = true;
        },
        updatePermissions: (state, action) => {
            state.permissions = action.payload.permissions;
            if (state.user && action.payload.overrides !== undefined) {
                state.user.overrides = action.payload.overrides;
            }
        },
    },
});
export const { loginSuccess, loginOtpPending, logout, verifyOtpSuccess, otpLoginSuccess, updateUserAndToken, updatePermissions } = authSlice.actions;
// Selectors - using RootState type from store
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectPermissions = (state) => state.auth.permissions;
export const selectIsOtpVerified = (state) => state.auth.isOtpVerified;
export const selectRequiresOtp = (state) => state.auth.requiresOtp;
export const selectOtpEntityType = (state) => state.auth.otpEntityType;
export const selectOtpUserId = (state) => state.auth.otpUserId;
export const selectEntityType = (state) => state.auth.entity_type;
// Marketing Calendar Specific Selectors
export const selectMarketingActions = (state) => {
    return state.auth.permissions?.marketing_calendar?.actions || [];
};
export const selectCanManageProposals = (state) => selectMarketingActions(state).includes("Manage Proposals");
export const selectCanDraftEmailApproval = (state) => selectMarketingActions(state).includes("Draft Email Approval");
export const selectSendForApproval = (state) => selectMarketingActions(state).includes("Send For Approval");
export const selectCanCalendarSync = (state) => selectMarketingActions(state).includes("Calendar Sync");
// Client List Specific Selectors
export const selectClientActions = (state) => state.auth.permissions?.clients?.actions || [];
export const selectCanManageClients = (state) => selectClientActions(state).includes("Manage Clients");
export const selectCanInviteClients = (state) => selectClientActions(state).includes("Invite Clients");
export const selectCanConnectNetworks = (state) => selectClientActions(state).includes("Connect Networks");
// Sales Reporting Specific Selectors — "Add Deal" gates the deal tracker (add/edit/delete),
// "Legacy Data Edit" gates editing BDR past-month (legacy) data cells. Refresh visibility is
// handled by LastUpdatedInfo via moduleName.
export const selectSalesReportingActions = (state) => state.auth.permissions?.sales_reporting?.actions || [];
export const selectCanAddDeal = (state) => selectSalesReportingActions(state).includes("Add Deal");
export const selectCanEditLegacyData = (state) => selectSalesReportingActions(state).includes("Legacy Data Edit");
// "AE"/"BDR" actions gate which report tabs are visible (both, one, or none).
export const selectCanViewAeReport = (state) => selectSalesReportingActions(state).includes("AE");
export const selectCanViewBdrReport = (state) => selectSalesReportingActions(state).includes("BDR");
export const selectIsSuperAdmin = (state) => state.auth.user?.role === "super_admin";
export const selectClientScope = (state, moduleName) => {
    if (state.auth.user?.role === "super_admin")
        return "all";
    return state.auth.permissions?.[moduleName]?.client_scope || "assigned";
};
// LTV Report Specific Selectors
export const selectLTVReportActions = (state) => state.auth.permissions?.ltv_report?.actions || [];
export const selectCanExportReport = (state) => selectLTVReportActions(state).includes("Segment Report Download");
// Create Report (Report Builder) Specific Selectors — "Manage Reports" gates
// add / edit / delete / send / schedule on the Create Report listing. Without
// it the page is read-only (View action only).
export const selectCreateReportActions = (state) => state.auth.permissions?.create_report?.actions || [];
export const selectCanManageReports = (state) => selectCreateReportActions(state).includes("Manage Reports");
export const selectCanManageAutoSchedule = (state) => selectCreateReportActions(state).includes("Manage Auto Schedule");
// "Manage Notes" gates adding a report note for internal users; clients are unchanged.
export const selectCanManageNotes = (state) => selectCreateReportActions(state).includes("Manage Notes");
// Account Performance Specific Selectors
export const selectAccountPerformanceActions = (state) => state.auth.permissions?.account_performance?.actions || [];
export const selectHasSitewiseAccess = (state) => selectAccountPerformanceActions(state).includes("Sitewise Access");
export const selectHasRecalculationAccess = (state) => selectAccountPerformanceActions(state).includes("Recalculation Access");
export const selectHasModifierAccess = (state) => selectAccountPerformanceActions(state).includes("Modifier");
// ── Commission Tracker Specific Selectors ──
// The six action grants combine with client_scope (see selectCommissionClientScope):
//   - "Full Access"    → both the listing (commission-tracker) and the report
//                        (commission-tracker-report) pages.
//   - "Limited Access" → the listing page only (report page is blocked).
//   - "Pacer Access"   → the Pacer tab is shown.
//   - "Paid Media" / "Affiliate" / "AEO" → restrict the client list to those
//     client types; applied on top of client_scope (all → filter all; assigned →
//     filter the assigned list). Used by Daily Update, Summary, and the report's
//     store list.
export const selectCommissionTrackerActions = (state) => state.auth.permissions?.commission_tracker?.actions || [];
export const selectCommissionHasFullAccess = (state) => selectCommissionTrackerActions(state).includes("Full Access");
export const selectCommissionHasLimitedAccess = (state) => selectCommissionTrackerActions(state).includes("Limited Access");
export const selectCommissionHasPacerAccess = (state) => selectCommissionTrackerActions(state).includes("Pacer Access");
export const selectCommissionHasPaidMediaAccess = (state) => selectCommissionTrackerActions(state).includes("Paid Media");
export const selectCommissionHasAffiliateAccess = (state) => selectCommissionTrackerActions(state).includes("Affiliate");
export const selectCommissionHasAeoAccess = (state) => selectCommissionTrackerActions(state).includes("AEO");
// Report (inner) page is allowed with Full Access; listing (outer) with either grant.
export const selectCommissionCanViewReport = (state) => selectCommissionHasFullAccess(state);
export const selectCommissionCanViewListing = (state) => selectCommissionHasFullAccess(state) || selectCommissionHasLimitedAccess(state);
export const selectCommissionClientScope = (state) => selectClientScope(state, "commission_tracker");
// Billing Report Specific Selectors
export const selectBillingReportActions = (state) => state.auth.permissions?.billing_summary?.actions || [];
export const selectBillingHasFullAccess = (state) => selectBillingReportActions(state).includes("Full Access");
// Outreach Performance Specific Selectors
export const selectOutreachPerformanceActions = (state) => state.auth.permissions?.outreach_performance?.actions || [];
export const selectCanManagePlacements = (state) => selectIsSuperAdmin(state) || selectOutreachPerformanceActions(state).includes("Manage Placements");
export const selectCanRefreshOutreach = (state) => selectIsSuperAdmin(state) || Boolean(state.auth.permissions?.outreach_performance?.refresh);
// Publisher Reporting Specific Selectors
export const selectCanManagePublisherReportingPlacements = (state) => selectIsSuperAdmin(state) || (state.auth.permissions?.publisher_reporting?.actions || []).includes("Manage Placements");
export default authSlice.reducer;
